import { getDateWithTimezoneFixed, readableDate, subscribe as subscribeBase, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { subDays } from 'date-fns';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { client } from '../../config/api';
import { onSubscriptionData, subscribe } from '../../config/cache';
import {
  EventFieldsFragmentDoc,
  GetEventDocument,
  OnAnyDependantChangedDocument,
  OnAnyEventChangedDocument,
  TAddressParentTypes,
  TDeliveryMethod,
  TEventFieldsFragment,
  TEventTypes,
  TGetEventQuery,
  TGetEventQueryVariables,
  TListClientOneOffEventsQuery,
  TListClientOneOffEventsQueryVariables,
  TListEventsQuery,
  TListEventsQueryVariables,
  TListNotSkippedEventsWithoutGiftQuery,
  TListNotSkippedEventsWithoutGiftQueryVariables,
  TListSkippedEventsNewQuery,
  TListSkippedEventsNewQueryVariables,
  TListSkippedEventsQuery,
  TListSkippedEventsQueryVariables,
  TOnAnyEventChangedSubscription,
  TOnAnyEventChangedSubscriptionVariables,
  useGetEventLazyQuery,
  useListClientOneOffEventsLazyQuery,
  useListEventsLazyQuery,
  useListNotSkippedEventsWithoutGiftLazyQuery,
  useListSkippedEventsLazyQuery,
  useListSkippedEventsNewLazyQuery,
  useSearchEventsByRecipientLazyQuery
} from '../../generated/graphql';
import { TAddressType, useAllAddressesMap } from '../addresses/queries';
import { useAllCompaniesMapNEW } from '../companies/queries';
import { TDependantType, useAllDependantsMap } from '../dependants/queries';
import { TEmployeeType, useAllEmployeesMap, useSubscribeToEmployeeChanged } from '../employees/queries';
import { TOfficeType } from '../office/queries';

import { calculatedShippingDate } from '../gifts/shared';
import { useFragmentOrFetch } from '../shared/useFragmentOrFetch';
import { useQueryAll } from '../shared/useQueryAll';

export const onEventSubscriptionData = async (data?: TOnAnyEventChangedSubscription, vars?: TOnAnyEventChangedSubscriptionVariables) => {
  const { companyId, id, _deleted } = data?.onAnyEventChanged || {};

  if (_deleted) return onSubscriptionData(data as Record<string, any>, vars);
  if (!companyId || !id) return;

  const response = await client.query<TGetEventQuery, TGetEventQueryVariables>({
    query: GetEventDocument,
    variables: {
      id,
      companyId
    }
  });

  const eventData = response.data.getEvent;

  if (eventData) {
    const subscriptionData = {
      onAnyEventChanged: eventData as TConnectionItem
    };

    // CHANGED DATA FOR QUERIES THAT DEPEND ON NO VARIABLE (SINCE VARS SHOULD BE AN EMPTY OBJECT IN THIS SITUATION)
    onSubscriptionData(subscriptionData, vars);
  }
};

let onAnyEventChangedSubscribed = false;

export const useSubscribeToEventChanges = () => {
  useSubscribeToEmployeeChanged();

  useEffect(() => {
    subscribe({
      query: OnAnyDependantChangedDocument
    });
  }, []);

  useEffect(() => {
    if (!onAnyEventChangedSubscribed) {
      subscribeBase({
        client,
        query: OnAnyEventChangedDocument,
        onSubscriptionData: (data, vars) => {
          return onEventSubscriptionData(data as TOnAnyEventChangedSubscription, vars as TOnAnyEventChangedSubscriptionVariables);
        }
      });

      onAnyEventChangedSubscribed = true;
    }
  }, []);
};

// LIST

export const useFullEvents = <T extends TEventFieldsFragment>(events: T[]) => {
  const { data: companiesMap, error: companiesError, loading: companiesLoading } = useAllCompaniesMapNEW();

  console.log('companiesMap', companiesMap);

  const { data: employeesMap, error: employeesError, loading: employeesLoading } = useAllEmployeesMap();

  const { data: dependantsMap, error: dependantsError, loading: dependantsLoading } = useAllDependantsMap();

  const { data: addressesMap, error: addressesError, loading: addressesLoading } = useAllAddressesMap();

  const fullEvents = useMemo(() => {
    return events.map((e) => {
      let recipient: (typeof employeesMap)[string] | (typeof dependantsMap)[string] | undefined;

      if (e.recipientId != null) {
        const [tableName, recipientId] = e.recipientId.split('|');

        if (tableName === import.meta.env.REACT_APP_DEPENDANTS_TABLE_NAME) recipient = dependantsMap[recipientId];
        else if (tableName === import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME) recipient = employeesMap[recipientId];
      }

      const address = e.addressId ? addressesMap[e.addressId] : undefined;

      return {
        ...e,
        company: e.companyId ? companiesMap[e.companyId] : undefined,
        employee: e.employeeId ? employeesMap[e.employeeId] : undefined,
        address,
        recipient: recipient,
        shippingDate: calculatedShippingDate({
          date: e.eventDate,
          address: { country: address?.country as string, state: address?.state as string }
        })
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressesMap, companiesMap, dependantsMap, employeesMap, JSON.stringify(events)]);

  return {
    data: fullEvents,
    error: companiesError || employeesError || dependantsError || addressesError,
    loading: companiesLoading || employeesLoading || dependantsLoading || addressesLoading
  };
};

export const useQueryAllUpcomingEvents = () => {
  useSubscribeToEventChanges();

  const { data, error, loading } = useQueryAll<TListNotSkippedEventsWithoutGiftQuery, TListNotSkippedEventsWithoutGiftQueryVariables>({
    useQuery: useListNotSkippedEventsWithoutGiftLazyQuery
  });

  const { data: fullEvents, error: fullError, loading: fullLoading } = useFullEvents(_.compact(data?.listNotSkippedEventsWithoutGift.items));

  const events = useMemo(() => {
    return fullEvents
      .filter((e) => e.giftId == null && e.skippedAt == null && !e.isPaused && e.company?.isLive && (e.company?.endDate == null || readableDate(e.company.endDate, 'yyyy-MM-dd') >= readableDate(new Date(), 'yyyy-MM-dd')))
      .sort((a, b) => {
        return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
      });
  }, [fullEvents]);

  return {
    data: events,
    error: error || fullError,
    loading: loading || fullLoading
  };
};

export const useNewHiresUpcomingEvents = () => {
  const { data, error, loading } = useQueryAllUpcomingEvents();

  const newHiresUpcomingEvents = useMemo(() => {
    return data.filter((e) => e.type === TEventTypes.NewHire);
  }, [data]);

  return {
    data: newHiresUpcomingEvents,
    error,
    loading
  };
};

export const useSubscriptionUpcomingEvents = () => {
  const { data, error, loading } = useQueryAllUpcomingEvents();

  const subscriptionUpcomingEvents = useMemo(() => {
    return data.filter((e) => e.type === TEventTypes.WorkAnniversary || e.type === TEventTypes.Birthday);
  }, [data]);

  return {
    data: subscriptionUpcomingEvents,
    error,
    loading
  };
};

export const useUpcomingSubscriptionOfficeEvents = () => {
  const { data, error, loading } = useSubscriptionUpcomingEvents();

  const officeEvents = useMemo(() => {
    return data.filter((e) => {
      if (e.address != null) return e.address?.parentType !== TAddressParentTypes.Employee;
      else return e.company?.deliveryMethod !== TDeliveryMethod.Home;
    });
  }, [data]);

  return {
    data: officeEvents,
    error,
    loading
  };
};

export const useUpcomingSubscriptionHomeEvents = () => {
  const { data, error, loading } = useSubscriptionUpcomingEvents();

  const homeEvents = useMemo(() => {
    return data.filter((e) => {
      if (e.address != null) return e.address?.parentType === TAddressParentTypes.Employee;
      else return e.company?.deliveryMethod === TDeliveryMethod.Home;
    });
  }, [data]);

  return {
    data: homeEvents,
    error,
    loading
  };
};

export const useUpcomingOneOffEvents = () => {
  const { data, error, loading } = useQueryAllUpcomingEvents();

  const oneOffEvents = useMemo(() => {
    return data.filter((e) => {
      return e.type !== TEventTypes.NewHire && e.type !== TEventTypes.WorkAnniversary && e.type !== TEventTypes.Birthday;
    });
  }, [data]);

  return {
    data: oneOffEvents,
    error,
    loading
  };
};

export const useGroupedUpcomingSubscriptionHomeEvents = () => {
  const { data, error, loading } = useUpcomingSubscriptionHomeEvents();

  const [groupedHomeEvents, flatHomeEvents] = useMemo(() => {
    let grouped: TEventType[] = [];
    const flat: typeof data = [];

    Object.values(_.groupBy(data, (e) => e.addressId || JSON.stringify(e))).map((items) => {
      const newItems: TEventType[] = [];

      for (const item of items) {
        const itemDate = getDateWithTimezoneFixed(item.eventDate);
        if (!itemDate) continue;
        const lowerDate = subDays(itemDate, 14);

        const firstMatchingDateIndex = newItems.findIndex((newItem) => {
          const d = getDateWithTimezoneFixed(newItem.eventDate);
          return !!d && d.getTime() >= lowerDate.getTime() && d.getTime() <= itemDate.getTime();
        });

        const firstMatchingDate = newItems[firstMatchingDateIndex];

        if (firstMatchingDate) {
          if (firstMatchingDate.subRows == null) firstMatchingDate.subRows = [];

          const eventData = { ...item, groupedToEventId: firstMatchingDate.id };
          firstMatchingDate.subRows.push(_.cloneDeep(eventData) as unknown as TEventType);
          if (firstMatchingDate.groupedToEventId == null) {
            const flatIndex = flat.findIndex((e) => e.id === firstMatchingDate.id);
            if (flatIndex > -1) flat[flatIndex].groupedToEventId = firstMatchingDate.id;

            newItems[firstMatchingDateIndex].groupedToEventId = firstMatchingDate.id;
          }
          flat.push(_.cloneDeep(eventData));
        } else {
          newItems.push(_.cloneDeep(item) as unknown as TEventType);
          flat.push(_.cloneDeep(item));
        }
      }

      grouped = grouped.concat(newItems);
    });

    return [
      grouped.sort((a, b) => {
        const dateA = a.shippingDate ? new Date(a.shippingDate).getTime() : new Date().getTime();
        const dateB = b.shippingDate ? new Date(b.shippingDate).getTime() : new Date().getTime();
        const shippingDateDiff = dateA - dateB;
        if (shippingDateDiff === 0) return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
        else return shippingDateDiff;
      }),
      flat
    ];
  }, [data]);

  return {
    data: groupedHomeEvents,
    flatData: flatHomeEvents,
    error,
    loading
  };
};

export const useQueryAllSkippedEvents = () => {
  useSubscribeToEventChanges();

  const { data, error, loading } = useQueryAll<TListSkippedEventsQuery, TListSkippedEventsQueryVariables>({
    useQuery: useListSkippedEventsLazyQuery
  });

  const { data: fullEvents, error: fullError, loading: fullLoading } = useFullEvents(_.compact(data?.listSkippedEvents.items));

  const events = useMemo(() => {
    return fullEvents.filter((e) => e.skippedAt != null && e.giftId == null);
  }, [fullEvents]);

  return {
    data: events,
    error: error || fullError,
    loading: loading || fullLoading
  };
};

// ITEMS

export const useFragmentOrFetchEvent = (variables: TGetEventQueryVariables) => {
  useSubscribeToEventChanges();

  const { data, error, loading } = useFragmentOrFetch<TEventFieldsFragment, TGetEventQuery, TGetEventQueryVariables, TOnAnyEventChangedSubscriptionVariables>({
    useLazyQuery: useGetEventLazyQuery,
    variables,
    fragmentDoc: EventFieldsFragmentDoc,
    __typename: 'Event'
  });

  const { data: events, error: fullError, loading: fullLoading } = useFullEvents(_.compact([data]) as TEventFieldsFragment[]);

  return {
    data: events[0],
    error: error || fullError,
    loading: loading || fullLoading
  };
};

// =========================
// NEW
// =========================

export type TEventType = TEventFieldsFragment & {
  company?: Record<string, any>;
  office?: TOfficeType;
  employee?: TEmployeeType;
  address?: TAddressType;
  recipient?: TEmployeeType | TDependantType;
  subRows?: TEventType[];
};

export type TUseUpcomingEventsNewProps = {
  includingNextDays?: number;
  includingLastDays?: number;
  companyId?: string;
};

export const useUpcomingSubscriptionHomeEventsOLD = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const {
    data: dataWorkAnniversary,
    error: errorWorkAnniversary,
    loading: loadingWorkAnniversary
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'workAnniversary',
      eventPage: 'home',
      includingLastDays,
      includingNextDays,
      limit: 100
    }
  });

  const {
    data: dataBirthday,
    error: errorBirthday,
    loading: loadingBirthday
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'birthday',
      eventPage: 'home',
      includingLastDays,
      includingNextDays,
      limit: 100
    }
  });

  const events = useMemo(() => {
    return [...(dataBirthday?.listEvents.items || []), ...(dataWorkAnniversary?.listEvents.items || [])];
  }, [dataBirthday, dataWorkAnniversary]);

  return {
    data: events as unknown as TEventType[],
    error: errorWorkAnniversary || errorBirthday,
    loading: loadingWorkAnniversary || loadingBirthday
  };
};

export const useUpcomingSubscriptionHomeEventsNEW = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const {
    data: dataWorkAnniversary,
    error: errorWorkAnniversary,
    loading: loadingWorkAnniversary,
    nextToken: workanniversaryNextToken,
    fetchMore: workAnniversaryFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'workAnniversary',
      eventPage: 'home',
      limit: 100,
      includingLastDays,
      includingNextDays
    },
    isScroll: true
  });

  const {
    data: dataBirthday,
    error: errorBirthday,
    loading: loadingBirthday,
    nextToken: birthdayNextToken,
    fetchMore: birthdayFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'birthday',
      eventPage: 'home',
      limit: 100,
      includingLastDays,
      includingNextDays
    },
    isScroll: true
  });

  const events = useMemo(() => {
    return [...(dataBirthday?.listEvents?.items || []), ...(dataWorkAnniversary?.listEvents?.items || [])].filter((e) => {
      if (e?.employee?.isPaused) return false;
      return true;
    });
  }, [dataBirthday, dataWorkAnniversary]);

  return {
    data: events as unknown as TEventType[],
    error: errorWorkAnniversary || errorBirthday,
    loading: loadingWorkAnniversary || loadingBirthday,
    nextToken: {
      workanniversaryNextToken,
      birthdayNextToken
    },
    fetchMore: {
      workAnniversaryFetchMore,
      birthdayFetchMore
    }
  };
};

export const useUpcomingSubscriptionOfficeEventsOLD = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const {
    data: dataWorkAnniversary,
    error: errorWorkAnniversary,
    loading: loadingWorkAnniversary
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'workAnniversary', eventPage: 'office', limit: 100, includingLastDays, includingNextDays }
  });

  const {
    data: dataBirthday,
    error: errorBirthday,
    loading: loadingBirthday
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'birthday', eventPage: 'office', limit: 100, includingLastDays, includingNextDays }
  });

  const events = useMemo(() => {
    return [...(dataBirthday?.listEvents?.items || []), ...(dataWorkAnniversary?.listEvents?.items || [])].filter((e) => {
      if (e?.employee?.isPaused) return false;
      return true;
    });
  }, [dataBirthday, dataWorkAnniversary]);

  return {
    data: events as unknown as TEventType[],
    error: errorWorkAnniversary || errorBirthday,
    loading: loadingWorkAnniversary || loadingBirthday
  };
};

export const useUpcomingSubscriptionOfficeEventsNEW = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const {
    data: dataWorkAnniversary,
    error: errorWorkAnniversary,
    loading: loadingWorkAnniversary,
    nextToken: workanniversaryNextToken,
    fetchMore: workAnniversaryFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'workAnniversary', eventPage: 'office', limit: 100, includingLastDays, includingNextDays },
    isScroll: true
  });

  const {
    data: dataBirthday,
    error: errorBirthday,
    loading: loadingBirthday,
    fetchMore: birthdayFetchMore,
    nextToken: birthdayNextToken
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'birthday', eventPage: 'office', limit: 100, includingLastDays, includingNextDays },
    isScroll: true
  });

  const events = useMemo(() => {
    return [...(dataBirthday?.listEvents?.items || []), ...(dataWorkAnniversary?.listEvents?.items || [])].filter((e) => {
      if (e?.employee?.isPaused) return false;
      return true;
    });
  }, [dataBirthday, dataWorkAnniversary]);

  return {
    data: events as unknown as TEventType[],
    error: errorWorkAnniversary || errorBirthday,
    loading: loadingWorkAnniversary || loadingBirthday,
    fetchMore: {
      workAnniversaryFetchMore,
      birthdayFetchMore
    },
    nextToken: {
      workanniversaryNextToken,
      birthdayNextToken
    }
  };
};

export const useNewHiresUpcomingEventsOLD = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'newHire',
      eventPage: 'newHire',
      includingLastDays,
      includingNextDays,
      limit: 100
    }
  });

  const events = useMemo(() => {
    return [...(data?.listEvents?.items || [])].filter((e) => {
      if (e?.employee?.isPaused) return false;
      return true;
    });
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading
  };
};

export const useNewHiresUpcomingEventsNEW = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading, nextToken, fetchMore } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'newHire', eventPage: 'newHire', limit: 100, includingLastDays, includingNextDays },
    isScroll: true
  });

  const events = useMemo(() => {
    return [...(data?.listEvents?.items || [])].filter((e) => {
      if (e?.employee?.isPaused) return false;
      return true;
    });
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading,
    nextToken,
    fetchMore
  };
};

export const useOneOffUpcomingEventsNEW = (props: TUseUpcomingEventsNewProps & { isScroll?: boolean }) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays, isScroll = true } = props;

  const {
    data: babyGifts,
    error: babyError,
    loading: babyLoading,
    nextToken: babyNextToken,
    fetchMore: babyFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'babyGift', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: bereavementGifts,
    error: bereavementError,
    loading: bereavementLoading,
    nextToken: bereavementNextToken,
    fetchMore: bereavementFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'bereavement', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll
  });

  const {
    data: weddingGifts,
    error: weddingError,
    loading: weddingLoading,
    nextToken: weddingNextToken,
    fetchMore: weddingFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'wedding', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: replacementWAGifts,
    error: replacementWAError,
    loading: replacementWALoading,
    nextToken: replacementWANextToken,
    fetchMore: replacementWAFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'replacementWorkAnniversary',
      eventPage: 'oneOff',
      limit: 100,
      includingLastDays,
      includingNextDays
    },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: replacementEBGifts,
    error: replacementEBError,
    loading: replacementEBLoading,
    nextToken: replacementEBNextToken,
    fetchMore: replacementEBFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: {
      type: 'replacementEmployeeBirthday',
      eventPage: 'oneOff',
      limit: 100,
      includingLastDays,
      includingNextDays
    },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: careGifts,
    error: careError,
    loading: careLoading,
    nextToken: careNextToken,
    fetchMore: careFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'carePackage', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: customBricksGifts,
    error: customBricksError,
    loading: customBricksLoading,
    nextToken: customBricksNextToken,
    fetchMore: customBricksFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'customBricks', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const {
    data: otherGifts,
    error: otherError,
    loading: otherLoading,
    nextToken: otherNextToken,
    fetchMore: otherFetchMore
  } = useQueryAll<TListEventsQuery, TListEventsQueryVariables>({
    useQuery: useListEventsLazyQuery,
    variables: { type: 'other', eventPage: 'oneOff', limit: 100, includingLastDays, includingNextDays },
    isScroll,
    fetchPolicy: 'network-only'
  });

  const events = useMemo(
    () =>
      [
        ...(babyGifts?.listEvents?.items || []),
        ...(bereavementGifts?.listEvents?.items || []),
        ...(weddingGifts?.listEvents?.items || []),
        ...(replacementWAGifts?.listEvents?.items || []),
        ...(replacementEBGifts?.listEvents?.items || []),
        ...(careGifts?.listEvents?.items || []),
        ...(customBricksGifts?.listEvents?.items || []),
        ...(otherGifts?.listEvents?.items || [])
      ].filter((e) => {
        if (e?.employee?.isPaused) return false;
        return true;
      }),
    [babyGifts, bereavementGifts, weddingGifts, replacementWAGifts, replacementEBGifts, careGifts, customBricksGifts, otherGifts]
  );

  return {
    data: events as unknown as TEventType[],
    error: babyError || bereavementError || weddingError || replacementWAError || replacementEBError || careError || customBricksError || otherError,
    loading: babyLoading || bereavementLoading || weddingLoading || replacementWALoading || replacementEBLoading || careLoading || customBricksLoading || otherLoading,
    nextToken: {
      babyNextToken,
      bereavementNextToken,
      weddingNextToken,
      replacementWANextToken,
      replacementEBNextToken,
      careNextToken,
      customBricksNextToken,
      otherNextToken
    },
    fetchMore: {
      babyFetchMore,
      bereavementFetchMore,
      weddingFetchMore,
      replacementWAFetchMore,
      replacementEBFetchMore,
      careFetchMore,
      customBricksFetchMore,
      otherFetchMore
    }
  };
};

export const useClientOneOffUpcomingEvents = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading, refetch } = useQueryAll<TListClientOneOffEventsQuery, TListClientOneOffEventsQueryVariables>({
    useQuery: useListClientOneOffEventsLazyQuery,
    variables: {
      eventPage: 'oneOff',
      limit: 100,
      includingLastDays,
      includingNextDays
    }
  });

  const events = useMemo(() => {
    return _.compact(data?.listClientOneOffEvents?.items).filter((event) => {
      if (event.wasPaused) return true;
      if (event.employee?.isPaused) return false;
      return true;
    });
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading,
    refetch
  };
};

export const useClientOneOffUpcomingEventsNEW = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading, nextToken, fetchMore, refetch } = useQueryAll<TListClientOneOffEventsQuery, TListClientOneOffEventsQueryVariables>({
    useQuery: useListClientOneOffEventsLazyQuery,
    variables: {
      eventPage: 'oneOff',
      limit: 100,
      includingLastDays,
      includingNextDays
    },
    isScroll: true
  });

  const events = useMemo(() => {
    return _.compact(data?.listClientOneOffEvents?.items).filter((event) => {
      if (event.wasPaused) return true;
      if (event.employee?.isPaused) return false;
      return true;
    });
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading,
    nextToken,
    fetchMore,
    refetch
  };
};

export const useSkippedUpcomingEventsOLD = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading } = useQueryAll<TListSkippedEventsNewQuery, TListSkippedEventsNewQueryVariables>({
    useQuery: useListSkippedEventsNewLazyQuery,
    variables: { eventPage: 'skipped', limit: 100, includingLastDays, includingNextDays }
  });

  const events = useMemo(() => {
    return [...(data?.listSkippedEventsNew.items || [])];
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading
  };
};

export const useSkippedUpcomingEventsNEW = (props: TUseUpcomingEventsNewProps) => {
  useSubscribeToEventChanges();

  const { includingLastDays, includingNextDays } = props;

  const { data, error, loading, nextToken, fetchMore } = useQueryAll<TListSkippedEventsNewQuery, TListSkippedEventsNewQueryVariables>({
    useQuery: useListSkippedEventsNewLazyQuery,
    variables: { eventPage: 'skipped', limit: 100, includingLastDays, includingNextDays },
    isScroll: true
  });

  const events = useMemo(() => {
    return [...(data?.listSkippedEventsNew.items || [])];
  }, [data]);

  return {
    data: events as unknown as TEventType[],
    error,
    loading,
    nextToken,
    fetchMore
  };
};

// Fast, exact, server-side recipient-name search shared by every events screen -- replaces the
// client-side quickScore fuzzy filter with a real indexed backend query (see
// cs-api/functions/events/searchEventsByRecipient.ts). companyId is the partition key of the
// search index server-side, so both a company and a name must be present for this to run --
// there's no efficient way to search "across every company" at once.
//
// `types` picks which event types to search (each fires its own query, same as the screen's own
// listEvents calls do -- e.g. Home/Office search workAnniversary + birthday in parallel). Pass
// undefined/[] for screens that don't filter by type at all (Skipped mixes every type together).
// Only two type-scoped queries are supported at once (fixed hook count -- see Rules of Hooks);
// NewHire uses one, Skipped uses zero (searches with type: undefined, i.e. every type).
export type TUseSearchEventsByRecipientProps = {
  companyId?: string;
  recipientName?: string;
  eventPage: string;
  types?: string[];
  includingNextDays?: number;
  includingLastDays?: number;
};

export const useSearchEventsByRecipient = (props: TUseSearchEventsByRecipientProps) => {
  const { companyId, recipientName, eventPage, types, includingNextDays, includingLastDays } = props;

  const isActive = Boolean(companyId && recipientName);

  const [typeA, typeB] = types && types.length > 0 ? types : [undefined, undefined];

  const [searchA, { data: dataA, loading: loadingA, error: errorA }] = useSearchEventsByRecipientLazyQuery();
  const [searchB, { data: dataB, loading: loadingB, error: errorB }] = useSearchEventsByRecipientLazyQuery();

  useEffect(() => {
    if (!companyId || !recipientName) return;

    searchA({
      variables: { companyId, recipientName, eventPage, type: typeA, includingNextDays, includingLastDays }
    });

    if (typeB)
      searchB({
        variables: { companyId, recipientName, eventPage, type: typeB, includingNextDays, includingLastDays }
      });
  }, [companyId, recipientName, eventPage, typeA, typeB, includingNextDays, includingLastDays, searchA, searchB]);

  const events = useMemo(() => {
    return [...(dataA?.searchEventsByRecipient.items || []), ...(typeB ? dataB?.searchEventsByRecipient.items || [] : [])];
  }, [dataA, dataB, typeB]);

  return {
    isActive,
    data: events as unknown as TEventType[],
    error: errorA || (typeB ? errorB : undefined),
    loading: isActive && (loadingA || (typeB ? loadingB : false))
  };
};
